/**
 * AuditList — Sidebar-Panel für Audit-Objekte
 *
 * Liest _auditObjects aus dem globalen script.js-Scope (window._auditObjects)
 * und zeigt sie als klickbare Liste an. Klick → fliegt zur Audit-Box.
 * Suche + Filter nach Funktion und Potential.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'

// script.js legt _auditObjects als globales Array an
declare global {
  interface Window {
    _auditObjects: AuditObj[]
    showAuditDialog: (data?: any, onSave?: (d: any) => void) => void
    navigateToAuditClip: (clipSnapshot: any, auditId?: string) => void
    deleteAudit: (id: string) => void
    editAudit: (id: string) => void
  }
}

interface AuditObj {
  id: string
  data: {
    bezeichnung: string
    ebene: string
    funktion: string
    menge: number | null
    einheit: string
    zustand: string
    potentiale: string
    timestamp: string
    cloudId: string
    [key: string]: any
  }
  clipSnapshot: {
    position: { x: number; y: number; z: number }
    scale: { x: number; y: number; z: number }
  }
  label: HTMLElement
}

const FUNKTION_OPTIONS = ['Gebäudehülle', 'TGA', 'Tragstruktur', 'Ausbauteile', 'Andere']
const POTENTIAL_OPTIONS = ['Ja', 'Nein', 'keine abschließende Bewertung möglich']
const POTENTIAL_SHORT: Record<string, string> = {
  'Ja': 'Ja',
  'Nein': 'Nein',
  'keine abschließende Bewertung möglich': 'Offen',
}
const POTENTIAL_CLASS_MAP: Record<string, string> = {
  'Ja': '--green',
  'Nein': '--red',
  'keine abschließende Bewertung möglich': '--amber',
}
const POTENTIAL_COLOR_MAP: Record<string, string> = {
  'Ja': '#10b981',
  'Nein': '#ef4444',
  'keine abschließende Bewertung möglich': '#d97706',
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    })
  } catch { return iso }
}

function IconSearch() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  )
}

function IconX() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="4" y1="4" x2="20" y2="20"/><line x1="20" y1="4" x2="4" y2="20"/>
    </svg>
  )
}

export function AuditList({ currentCloudId, onEditAudit }: { currentCloudId: string | null; onEditAudit?: (id: string) => void }) {
  const [allAudits, setAllAudits] = useState<AuditObj[]>([])
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filterFunktion, setFilterFunktion] = useState<string | null>(null)
  const [filterPotential, setFilterPotential] = useState<string | null>(null)

  // Poll _auditObjects aus script.js
  // Kein cloudId-Filter nötig: loadAudits()/clearAudits() in script.js
  // stellt sicher, dass _auditObjects immer den richtigen Scope hat
  // (Einzelwolke → nur deren Audits, Projekt → alle Projekt-Audits).
  useEffect(() => {
    function sync() {
      const all = window._auditObjects || []
      setAllAudits([...all])
    }
    sync()
    const interval = setInterval(sync, 1000)
    return () => clearInterval(interval)
  }, [])

  // Vorhandene Funktionen/Potentiale ermitteln (für Filter-Chips)
  const usedFunktionen = useMemo(() => {
    const set = new Set(allAudits.map(a => a.data.funktion).filter(Boolean))
    return FUNKTION_OPTIONS.filter(f => set.has(f))
  }, [allAudits])

  const usedPotentiale = useMemo(() => {
    const set = new Set(allAudits.map(a => a.data.potentiale).filter(Boolean))
    return POTENTIAL_OPTIONS.filter(p => set.has(p))
  }, [allAudits])

  // Gefilterte Audits
  const filtered = useMemo(() => {
    let result = allAudits
    if (filterFunktion) result = result.filter(a => a.data.funktion === filterFunktion)
    if (filterPotential) result = result.filter(a => a.data.potentiale === filterPotential)
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(a =>
        (a.data.bezeichnung || '').toLowerCase().includes(q) ||
        (a.data.ebene || '').toLowerCase().includes(q) ||
        (a.data.funktion || '').toLowerCase().includes(q) ||
        (a.data.hersteller || '').toLowerCase().includes(q) ||
        (a.data.zustand || '').toLowerCase().includes(q) ||
        (a.data.schadstoff || '').toLowerCase().includes(q) ||
        (a.data.info || '').toLowerCase().includes(q)
      )
    }
    return result
  }, [allAudits, filterFunktion, filterPotential, search])

  const hasFilters = !!filterFunktion || !!filterPotential

  const handleClick = useCallback((audit: AuditObj) => {
    if (window.navigateToAuditClip && audit.clipSnapshot) {
      window.navigateToAuditClip(audit.clipSnapshot, audit.id)
    } else {
      audit.label?.click()
    }
  }, [])

  return (
    <div data-component="AuditList" className="ghost-panel">

      {/* ── Suche ─────────────────────────────────────────────── */}
      <div data-section="audit-search" className="ghost-search">
        <div className="ghost-search__inner">
          <span className="ghost-search__icon"><IconSearch /></span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Audits durchsuchen …"
            className="ghost-search__input"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="ghost-search__clear"
            >
              <IconX />
            </button>
          )}
        </div>
      </div>

      {/* ── Filter-Chips ──────────────────────────────────────── */}
      {(usedFunktionen.length > 0 || usedPotentiale.length > 0) && (
        <div data-section="audit-filters" className="ghost-filters">
          {/* Funktion */}
          {usedFunktionen.length > 0 && (
            <div className="ghost-filters__row">
              <span className="ghost-filters__label">Funktion</span>
              {usedFunktionen.map(f => {
                const active = filterFunktion === f
                return (
                  <button
                    key={f}
                    onClick={() => setFilterFunktion(active ? null : f)}
                    className={`ghost-chip ${active ? 'ghost-chip--active' : ''}`}
                  >
                    {f}
                  </button>
                )
              })}
            </div>
          )}

          {/* Potential */}
          {usedPotentiale.length > 0 && (
            <div className="ghost-filters__row">
              <span className="ghost-filters__label">Potential</span>
              {usedPotentiale.map(p => {
                const active = filterPotential === p
                const color = POTENTIAL_COLOR_MAP[p] || '#9ca3af'
                return (
                  <button
                    key={p}
                    onClick={() => setFilterPotential(active ? null : p)}
                    className={`ghost-chip ${active ? 'ghost-chip--active' : ''}`}
                    style={{
                      borderColor: active ? color + 'aa' : color + '33',
                      background:  active ? color + '22' : 'transparent',
                      color:       active ? color : '#6b7280',
                    }}
                  >
                    {POTENTIAL_SHORT[p] || p}
                  </button>
                )
              })}
            </div>
          )}

          {/* Reset */}
          {hasFilters && (
            <button
              onClick={() => { setFilterFunktion(null); setFilterPotential(null) }}
              className="ghost-filters__reset"
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}

      {/* ── Liste ─────────────────────────────────────────────── */}
      <div data-section="audit-items" className="ghost-list">
        {filtered.length === 0 ? (
          <div className="ghost-empty">
            {allAudits.length === 0 ? (
              <>
                <div className="ghost-empty__hint">
                  Keine Audits vorhanden
                </div>
                <div className="ghost-empty__hint">
                  Klicke auf "+" oder Rechtsklick → Detail extrahieren → Audit erstellen
                </div>
              </>
            ) : (
              <div className="ghost-empty__hint">
                Keine Audits für diesen Filter
              </div>
            )}
          </div>
        ) : (
          filtered.map(audit => {
            const d = audit.data
            return (
              <div
                key={audit.id}
                data-element="audit-row"
                data-audit-id={audit.id}
                onMouseEnter={() => setHoveredId(audit.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => handleClick(audit)}
                className="ghost-item"
              >
                {/* Content */}
                <div className="ghost-item__content">
                  <div className="ghost-item__title">
                    {d.bezeichnung || 'Audit'}
                  </div>

                  <div className="ghost-item__meta">
                    {d.ebene && <span>{d.ebene}</span>}
                    {d.funktion && (
                      <span className="ghost-item__meta-detail">
                        {d.funktion}
                      </span>
                    )}
                  </div>

                  <div className="ghost-item__meta">
                    {d.menge != null && d.einheit && (
                      <span>{d.menge} {d.einheit}</span>
                    )}
                    {d.timestamp && (
                      <span>{formatDate(d.timestamp)}</span>
                    )}
                  </div>
                </div>

                {d.potentiale && (
                  <div className="ghost-item__aside">
                    <div className={`ghost-item__badge ghost-item__potential ghost-item__potential${POTENTIAL_CLASS_MAP[d.potentiale] || ''}`}>
                      {POTENTIAL_SHORT[d.potentiale] || d.potentiale}
                    </div>
                  </div>
                )}

                {/* Hover-Actions: Edit + Delete */}
                {hoveredId === audit.id && (
                  <div
                    className="ghost-actions"
                    onClick={e => e.stopPropagation()}
                  >
                    <AuditAction
                      title="Bearbeiten"
                      data-element="btn-edit-audit"
                      onClick={() => onEditAudit ? onEditAudit(audit.id) : window.editAudit?.(audit.id)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                      </svg>
                    </AuditAction>
                    <AuditAction
                      title="Löschen"
                      data-element="btn-delete-audit"
                      danger
                      onClick={() => {
                        if (confirm(`Audit „${d.bezeichnung || 'Audit'}" wirklich löschen?`)) {
                          window.deleteAudit?.(audit.id)
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </AuditAction>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* ── Footer: Anzahl ────────────────────────────────────── */}
      {allAudits.length > 0 && (
        <div className="ghost-footer">
          {filtered.length === allAudits.length
            ? `${allAudits.length} Audit${allAudits.length !== 1 ? 's' : ''}`
            : `${filtered.length} / ${allAudits.length} Audits`}
        </div>
      )}
    </div>
  )
}

// ─── AuditAction (icon-only hover button) ──────────────────────────────────

function AuditAction({
  title,
  danger,
  onClick,
  children,
  'data-element': dataElement,
}: {
  title: string
  danger?: boolean
  onClick: () => void
  children: React.ReactNode
  'data-element'?: string
}) {
  return (
    <button
      title={title}
      data-element={dataElement}
      onClick={e => { e.stopPropagation(); onClick() }}
      className={`ghost-action-btn ${danger ? 'ghost-action-btn--danger' : ''}`}
    >
      {children}
    </button>
  )
}
